import { Button } from "@/components/ui/button";
import { FictionalHome, fmt } from "@/hooks/useHomeBuyingSim";
import { ArrowLeft } from "lucide-react";

interface Props {
  home: FictionalHome;
  pct: number;
  onPctChange: (p: number) => void;
  downPayment: number;
  loanAmount: number;
  liquidBefore: number;
  onContinue: () => void;
  onBack: () => void;
}

const OPTIONS = [0.03, 0.05, 0.10, 0.20];

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${highlight ? 'text-primary' : 'text-foreground'}`}>{value}</span>
    </div>
  );
}

export default function DownPaymentScreen({ home, pct, onPctChange, downPayment, loanAmount, liquidBefore, onContinue, onBack }: Props) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Down Payment</h2>
        <p className="text-sm text-muted-foreground">Choose how much you want to put down on the {home.home_name}.</p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {OPTIONS.map(o => (
          <button
            key={o}
            onClick={() => onPctChange(o)}
            className={`py-3 px-2 rounded-xl border text-center transition-all ${
              pct === o ? 'bg-primary text-primary-foreground border-primary' : 'glass-card hover:border-primary/50'
            }`}
          >
            <p className="font-heading font-bold text-sm">{Math.round(o * 100)}%</p>
            <p className="text-xs mt-1 opacity-80">{fmt(Math.round(home.home_price * o))}</p>
          </button>
        ))}
      </div>
      <div className="glass-card p-4 rounded-xl space-y-3">
        <Row label="Home Price" value={fmt(home.home_price)} />
        <Row label="Down Payment" value={fmt(downPayment)} highlight />
        <Row label="Loan Amount" value={fmt(loanAmount)} />
        <div className="border-t border-border pt-3">
          <Row label="Liquid Cash (Before)" value={fmt(liquidBefore)} />
          <Row label="Remaining After Down Payment" value={fmt(liquidBefore - downPayment)} />
        </div>
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} className="flex-1">Continue to Closing Costs</Button>
      </div>
    </div>
  );
}
